<template>
  <section v-if="assets" class="t-mt-8">
    <div role="heading">
      <h2 class="t-text-2xl t-font-medium t-text-gray-900 t-mb-1">
        My Assets
      </h2>
      <p class="t-text-sm t-font-medium t-text-gray-700 t-mb-0">
        Know where your assets are.
        <span v-tooltip="`Keep track of your assets (e.g. car, van, trailer, forklift, etc.) for only $2 (+GST) per week. Simply attach a Freightmate tracker to your asset, add the asset here, and you'll know where it is at all times.`">
          <i class="fa fa-question-circle d-inline" aria-hidden="true" style="margin-left: 0.25rem; white-space: pre-line"/>
        </span>
      </p>
      <link-track-to-business @trackerAdded="loadAssets" class="t-flex t-justify-end"/>
    </div>

    <div
      v-if="isLoadingAssets"
      class="t-mt-8 t-flex t-justify-center"
    >
      <p class="t-text-2xl">Loading Assets...</p>
    </div>

    <div
      v-if="!isLoadingAssets && !assets.length"
      class="t-mt-8 t-flex t-justify-center"
    >
      <p class="t-text-1xl">No Assets Yet</p>
    </div>

    <base-tracking-table
      v-show="!isLoadingAssets && assets.length"
      :trackers="assets"
      :column-headers="assetColumnHeaders"
      class="t-mt-8 t-mb-8"
    >
      <template #asset="{ assetName }">
        <div class="t-py-4 t-text-sm">
          <div class="t-grid t-grid-rows-1 t-grid-cols-2 t-visible t-block md:t-hidden">
            <p>Asset</p>
            <p>{{ assetName }}</p>
          </div>
          <p class="t-hidden md:t-inline">
            {{ assetName }}
          </p>
        </div>
      </template>

      <template #location="{ tracker }">
        <div class="t-py-4 t-text-sm">
          <div class="t-grid t-grid-rows-1 t-grid-cols-2 t-visible t-block md:t-hidden">
            <p>Current Location</p>
            <p>{{ tracker.currentLocation }}</p>
          </div>
          <p class="t-hidden md:t-inline">
            {{ tracker.currentLocation }}
          </p>
        </div>
      </template>

      <template #options="{ tracker }">
        <div class="t-text-sm t-flex-row t-justify-center t-items-center t-space-y-2 t-my-auto t-py-1 lg:t-flex-col">
          <button
            @click.prevent="onEditClick(tracker)"
            class="t-font-bold t-text-blue-500 t-py-1 t-px-2 t-rounded hover:t-text-blue-600"
          >
            Edit
          </button>

          <button
            @click.prevent="onDeleteClick(tracker)"
            class="t-font-bold t-text-red-500 t-py-1 t-px-2 t-rounded hover:t-text-red-600"
          >
            Delete
          </button>
        </div>
      </template>
    </base-tracking-table>

    <tracking-map v-if="!isLoadingAssets && assets.length" :trackers="assets"/>

    <modal
        v-if="showEditModal"
        @closed="showEditModal = !showEditModal"
    >
      <template #title>
        <h6 class="t-text-lg t-font-bold">Editing Asset: {{ selectedAsset.assetName }}</h6>
      </template>

      <div class="t-flex t-items-center t-px-4 t-pt-4 t-space-x-2">
        <label for="asset-name" class="t-text-gray-700 t-font-semibold">
          Asset Name:
        </label>
        <input v-model="assetName" name="asset-name" id="asset-name" type="text" class="t-px-2 t-border t-border-gray-400" placeholder="Asset">
      </div>

      <div class="t-px-4 t-mt-2 t-mb-4">
      <span v-if="!assetName || assetName === ''" class="t-text-red-600 t-font-medium t-text-sm t-tracking-wide">
          Please enter a name for the asset
        </span>
      </div>
      <div class="t-flex t-justify-end t-border-t t-rounded-md t-bg-gray-100 t-overflow-hidden">
        <button
          v-if="!isProcessingTrackerEdit"
          @click="editAsset"
          :disabled="!assetName || assetName === ''"
          class="t-mx-4 t-my-2 t-rounded t-px-3 t-py-2 t-text-white "
          :class="(!assetName || assetName === '') ? 't-bg-green-300': 't-bg-forest-green'"
        >
          Confirm
        </button>
        <button
          v-if="isProcessingTrackerEdit"
          disabled
          class="btn btn-success t-mx-4 t-my-2"
        >
          <i class="fa fa-spinner fa-spin"></i>
        </button>
      </div>
    </modal>

    <confirm-delete
      v-if="showDeleteModal"
      :title="'Confirming Asset Deletion'"
      :text="'Do you want to remove this asset from being tracked on your page?'"
      @cancelled="showDeleteModal = !showDeleteModal"
      @confirmed="deleteAssetTracker"
    >
    </confirm-delete>

  </section>
</template>

<script>
import BaseTrackingTable from "./BaseTrackingTable";
import TrackingMap from "./TrackingMap";
import ConfirmDelete from "../../utils/ConfirmDelete";
import LinkTrackToBusiness from "../../modals/LinkTrackToBusiness";
import Modal from "../../utils/Modal";

export default {
  name: 'AssetsTab',
  components: {
    BaseTrackingTable,
    TrackingMap,
    ConfirmDelete,
    LinkTrackToBusiness,
    Modal
  },
  props: {
    mapRawTrackers: {
      type: Function
    }
  },
  created() {
    this.loadAssets();
  },
  data() {
    return {
      assets: [],
      isLoadingAssets: false,
      assetColumnHeaders: ['Freightmate', 'Asset', 'Current Location', 'Last Detected', 'Last Movement', '', ''],
      selectedAsset: null,
      showEditModal: false,
      assetName: '',
      isProcessingTrackerEdit: false,
      isDeletingTracker: false,
      showDeleteModal: false,
    }
  },
  methods: {
    async loadAssets() {
      try {
        this.isLoadingAssets = true;
        let res = await axios.get('/send-freight/tracker/business');
        let data = res.data;
        this.assets = this.mapRawTrackers(data);
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingAssets = false;
      }
    },
    async editAsset() {
      if (!this.selectedAsset) {
        return;
      }

      try {
        this.isProcessingTrackerEdit = true;
        const params = {
          'asset_name': this.assetName
        };

        await axios.patch(`/send-freight/tracker/business/${this.selectedAsset.id}`, params);

        this.selectedAsset = null;
        this.assetName = '';
        this.showEditModal = false;

        await this.$swal({
          title: "Asset edited!",
          type: "success",
        });
        this.isProcessingTrackerEdit = false;
        await this.loadAssets();

      } catch (error) {
        console.error(error);
      }
    },
    async deleteAssetTracker() {
      if (!this.selectedAsset) {
        return;
      }

      try {
        this.isDeletingTracker = true;
        await axios.delete("/send-freight/tracker/business/" + this.selectedAsset.id);
        this.selectedAsset = null;
        this.showDeleteModal = false;
        await this.$swal({
          title: "Asset tracker deleted!",
          type: "success",
        });
        this.isDeletingTracker = false;
        await this.loadAssets();
      } catch (e) {
        await this.$swal({
          title: "Please try again or contact us",
          type: "error",
        });
        console.error(e);
      } finally {
        this.isDeletingTracker = false;
      }
    },
    onDeleteClick(selectedAsset) {
      this.selectedAsset = selectedAsset;
      this.showDeleteModal = true;
    },
    onEditClick(selectedAsset) {
      this.selectedAsset = selectedAsset;
      this.assetName = selectedAsset.assetName;
      this.showEditModal = true;
    }
  }
}
</script>

<style scoped>

</style>
